import { useEffect, useMemo, useRef, useState } from 'react';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ParkingaboProductTemplateSubscription } from './ParkingaboProductTemplateModels';
import {
    WizardBody,
    WizardBottomBar,
    WizardFadeInEffect,
    WizardProductHeader,
    WizardStepInfo,
    WizardStepper,
} from './WizardComponents';
import { useNavigate } from 'react-router-dom';
import { useLoggedParkingaboServerWrite } from '../../../api/ParkingaboApi';
import {
    RequestStatus,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage';
import { ensureIso, ensureIsoDate, Formatter } from 'dg-web-shared/lib/Date';
import { SelectableList } from '../../../components/SelectableList';
import {
    currencyCentsToLocalPrice,
    numberToLocalPrice,
} from 'dg-web-shared/lib/NumberFormatter';
import { FeedbackPopup } from '../../../components/FeedbackPopup';
import { ParkingaboOverlineList } from '../../../components/layout/ParkingaboOverlineList';
import { ParkingaboZoneInfo } from '../../../shared/ParkingaboProductModels';
import { intervalDurationToStartSelectOptions } from 'product-shared/product-template/SubscriptionIntervalDuration';
import { ProductZoneInfo, VehicleTypes } from './ProductsConfigurationOutlet';
import { useParkingaboAuthedPathGeneration } from '../../RouteUtils';
import {
    isSubscriptionProRataEnabled,
    SubscriptionProRataConfig,
} from 'product-shared/product-template/SubscriptionIntervalConfig.ts';
import { Language, makeLocalizedText } from 'dg-web-shared/lib/Localized.ts';

enum WizardStep {
    START_DATE,
    OVERVIEW,
}

interface WizardSelection {
    startDate: DateTime | null;
}

export function ProductsConfigurationSubscription({
    product,
    zones,
    noProductChange,
    refetchProducts,
}: {
    product: ParkingaboProductTemplateSubscription;
    zones: ParkingaboZoneInfo[];
    noProductChange: boolean;
    refetchProducts: () => void;
}) {
    const [selection, setSelection] = useState<WizardSelection>({
        startDate: null,
    });

    const [purchaseState, purchaseProduct, resetPurchaseState] =
        useLoggedParkingaboServerWrite<
            {
                contractTemplateId: number;
                startDate: string;
                monthlyRappenAmount: number;
                proRataPrice: number | null;
            },
            never
        >('purchase-subscription', () => ({
            url: `user/self/product/purchase/subscription`,
        }));

    useServerSuccessEffect(purchaseState, refetchProducts);
    const purchaseSelectionRef = useRef<WizardSelection | null>(null);
    useEffect(() => {
        if (
            purchaseState.status === RequestStatus.ERROR &&
            purchaseSelectionRef.current != selection
        ) {
            resetPurchaseState();
        }
    }, [selection, purchaseState, resetPurchaseState]);

    const navigate = useNavigate();
    const proRataPrice =
        selection.startDate && selection.startDate.day !== 1
            ? product.intervalConfig.proRataUntilEndOfMonthPrice
            : null;

    function handlePurchase() {
        if (!selection.startDate || activeStep !== WizardStep.OVERVIEW) {
            throw new Error(
                'Tried to purchase product without completed selection',
            );
        }
        const startIso = ensureIsoDate(selection.startDate);
        purchaseSelectionRef.current = selection;
        purchaseProduct({
            contractTemplateId: product.contractTemplateId,
            startDate: startIso,
            monthlyRappenAmount: product.intervalConfig.intervalPriceRappen,
            proRataPrice: proRataPrice,
        });
    }

    const steps: WizardStepInfo<WizardStep>[] = [
        {
            step: WizardStep.START_DATE,
            label: <Localized de="Datum" fr="Date" it="Data" en="Date" />,
        },
        {
            step: WizardStep.OVERVIEW,
            label: (
                <Localized
                    de="Übersicht"
                    fr="Aperçu"
                    it="Panoramica"
                    en="Overview"
                />
            ),
        },
    ];

    const firstStep = steps[0].step;
    const [activeStep, setActiveStep] = useState<WizardStep>(firstStep);

    const stepsWithClearance = useMemo(
        () => determineStepsWithClearance(selection),
        [selection],
    );

    const generateAuthedParkingaboPath = useParkingaboAuthedPathGeneration();

    return (
        <>
            <BackendRequestErrorMessage requestState={purchaseState} />
            <PurchaseSuccessDialog
                open={purchaseState.status === RequestStatus.SUCCESS}
                onClose={() =>
                    navigate(generateAuthedParkingaboPath('products'))
                }
            />
            <WizardProductHeader>
                <Localized {...product.name} />
            </WizardProductHeader>
            <WizardStepper
                steps={steps}
                stepsWithClearance={stepsWithClearance}
                activeStep={activeStep}
                onStepClick={setActiveStep}
                offset={noProductChange ? 0 : 3}
            />
            <WizardBody>
                {activeStep === WizardStep.START_DATE && (
                    <StartDateSelection
                        product={product}
                        selectedStartDate={selection.startDate}
                        onDateChange={date => {
                            setSelection({ startDate: date });
                            if (date) {
                                setActiveStep(activeStep + 1);
                            }
                        }}
                    />
                )}
                {activeStep === WizardStep.OVERVIEW && (
                    <ProductOverview
                        product={product}
                        zones={zones}
                        startDate={selection.startDate!}
                        proRataPrice={proRataPrice}
                    />
                )}
            </WizardBody>
            <WizardBottomBar
                onNextClick={
                    stepsWithClearance.includes(activeStep + 1)
                        ? () => {
                              if (activeStep === WizardStep.OVERVIEW) {
                                  handlePurchase();
                              } else {
                                  setActiveStep(activeStep + 1);
                              }
                          }
                        : undefined
                }
                nextLabel={
                    activeStep === WizardStep.OVERVIEW ? (
                        <Localized
                            de="Abonnieren"
                            fr="S'abonner"
                            it="Sottoscrivi"
                            en="Subscribe"
                        />
                    ) : undefined
                }
                nextAsSubmitRequestState={
                    activeStep === WizardStep.OVERVIEW
                        ? purchaseState.status
                        : undefined
                }
                onPreviousClick={() => {
                    if (activeStep === firstStep) {
                        navigate('..');
                    } else {
                        setActiveStep(activeStep - 1);
                    }
                }}
                previousLabel={
                    activeStep === firstStep ? (
                        <Localized
                            de="Produkt ändern"
                            fr="Changer de produit"
                            it="Cambia prodotto"
                            en="Change product"
                        />
                    ) : undefined
                }
                hidePrevious={noProductChange && activeStep === firstStep}
            />
        </>
    );
}

function determineStepsWithClearance(selection: WizardSelection) {
    const steps = Object.values(WizardStep).filter(
        (step): step is WizardStep =>
            typeof step !== 'string' && typeof step !== 'undefined',
    );
    const stepsIncludingLastNextStep = [...steps, steps[steps.length - 1] + 1];
    return stepsIncludingLastNextStep.filter(step => {
        switch (step) {
            case WizardStep.START_DATE:
                return true;
            default:
                return !!selection.startDate;
        }
    });
}

function StartDateSelection({
    product,
    selectedStartDate,
    onDateChange,
}: {
    product: ParkingaboProductTemplateSubscription;
    selectedStartDate: DateTime | null;
    onDateChange: (date: DateTime | null) => void;
}) {
    const { language } = useLanguage();
    const selectionItems = useMemo(() => {
        const now = DateTime.now();
        const proRataOption = subscriptionProRataOption(
            product.intervalConfig.subscriptionProRataConfig,
            now,
            language,
        );
        const startMonthOptions = Array.from(
            {
                length: product.intervalConfig
                    .maxIntervalsPurchaseAheadInFuture,
            },
            (_, k) => k + 1,
        ).map(amount =>
            intervalDurationToStartSelectOptions(
                now,
                amount,
                product.intervalConfig.intervalDuration,
                language,
            ),
        );
        return [...proRataOption, ...startMonthOptions].map(option => ({
            id: ensureIso(option.startDate),
            content: (
                <Typography
                    sx={{
                        fontWeight: 'bold',
                    }}
                >
                    {option.dateText}
                </Typography>
            ),
            date: option.startDate,
        }));
    }, [language, product]);

    function handleDurationChange(selectedIds: string[]) {
        const date =
            selectionItems.find(item => item.id === selectedIds[0])?.date ??
            null;
        onDateChange(date);
    }

    return (
        <WizardFadeInEffect>
            <SelectableList
                selectionItems={selectionItems}
                initialSelection={
                    selectedStartDate ? [ensureIso(selectedStartDate)] : []
                }
                onChange={handleDurationChange}
                preventDeselect
            />
        </WizardFadeInEffect>
    );
}

function subscriptionProRataOption(
    subscriptionProRataConfig: SubscriptionProRataConfig,
    now: DateTime,
    language: Language,
) {
    if (
        !isSubscriptionProRataEnabled(subscriptionProRataConfig) ||
        now.day === now.daysInMonth
    ) {
        return [];
    }
    const tomorrow = now.plus({ days: 1 }).startOf('day');
    const tomorrowString = Formatter.dayMonthYear(tomorrow);
    return [
        {
            startDate: tomorrow,
            dateText: makeLocalizedText(language)({
                de: `Morgen (${tomorrowString})`,
                fr: `Demain (${tomorrowString})`,
                it: `Domani (${tomorrowString})`,
                en: `Tomorrow (${tomorrowString})`,
            }),
        },
    ];
}

function ProductOverview({
    product,
    zones,
    startDate,
    proRataPrice,
}: {
    product: ParkingaboProductTemplateSubscription;
    zones: ParkingaboZoneInfo[];
    startDate: DateTime;
    proRataPrice: number | null;
}) {
    return (
        <WizardFadeInEffect>
            <ParkingaboOverlineList.Body>
                <ProductZoneInfo product={product} zones={zones} />
                <ParkingaboOverlineList.Item
                    label={
                        <Localized
                            de="Startdatum"
                            fr="Date de début"
                            it="Data di inizio"
                            en="Start date"
                        />
                    }
                >
                    {Formatter.dayMonthYear(startDate)}
                </ParkingaboOverlineList.Item>
                <ParkingaboOverlineList.Item
                    label={<Localized de="Ende" fr="Fin" it="Fine" en="End" />}
                >
                    <Localized
                        de="bis auf Widerruf"
                        fr="jusqu'à révocation"
                        it="fino a revoca"
                        en="until revocation"
                    />
                </ParkingaboOverlineList.Item>
                {product.info && (
                    <ParkingaboOverlineList.Item
                        label={
                            <Localized
                                de="Info"
                                fr="Info"
                                it="Info"
                                en="Info"
                            />
                        }
                    >
                        <Localized {...product.info} />
                    </ParkingaboOverlineList.Item>
                )}
                <VehicleTypes vehicleTypes={product.vehicleTypes} />
                {proRataPrice && (
                    <ParkingaboOverlineList.Item
                        label={
                            <Localized
                                de="Laufender Monat"
                                fr="Mois en cours"
                                it="Mese attuale"
                                en="Current month"
                            />
                        }
                    >
                        <Typography
                            sx={{
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                            }}
                        >
                            {numberToLocalPrice(proRataPrice)}
                            <Localized
                                de=" (einmalig)"
                                fr=" (une fois)"
                                it=" (una volta)"
                                en=" (once)"
                            />
                        </Typography>
                    </ParkingaboOverlineList.Item>
                )}
                <ParkingaboOverlineList.Item
                    label={
                        proRataPrice ? (
                            <Localized
                                de="Ab nächster Monat"
                                fr="Du mois prochain"
                                it="Dal mese prossimo"
                                en="Starting next month"
                            />
                        ) : (
                            <Localized
                                de="Preis"
                                fr="Prix"
                                it="Prezzo"
                                en="Price"
                            />
                        )
                    }
                >
                    <Typography
                        sx={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                        }}
                    >
                        {currencyCentsToLocalPrice(
                            product.intervalConfig.intervalPriceRappen,
                        )}
                        <Localized
                            de=" / Monat"
                            fr=" / mois"
                            it=" / mese"
                            en=" / month"
                        />
                    </Typography>
                </ParkingaboOverlineList.Item>
            </ParkingaboOverlineList.Body>
        </WizardFadeInEffect>
    );
}

function PurchaseSuccessDialog({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) {
    return (
        <FeedbackPopup
            open={open}
            color="success"
            title={
                <Localized
                    de="Bestätigung"
                    fr="Confirmation"
                    it="Conferma"
                    en="Confirmation"
                />
            }
            abortLabel={'OK'}
            onAbort={onClose}
        >
            <Localized
                de="Das Produkt wurde erfolgreich abonniert."
                fr="Le produit a été souscrit avec succès."
                it="Il prodotto è stato sottoscritto con successo."
                en="The product was successfully subscribed."
            />
        </FeedbackPopup>
    );
}
